import { useDispatch, useSelector } from "react-redux";
import "./Appointments.css";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import moment from 'moment';
import { Button } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { useBackIconProps } from "../../i18n/DirectionalIcons";
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useEffect, useState, useRef } from "react";
import { deleteAppointment} from "../../redux/actions/dataActions";
import Calendar from "./calendar/Calendar";
import AddToCalendar from "../common/AddToCalendar";
import { QRCodeSVG } from "qrcode.react";
import { useT } from "../../i18n";

// Waze brand mark (inline SVG) — a cyan speech-bubble smiley, recognizable as Waze.
function Appointment() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useT();
  const backIconProps = useBackIconProps();


  const [delOpen, setDelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // A6: embedded Google map, collapsed by default — opens downward under the nav links
  // (there's no room beside them). Keyless embed (?output=embed) → no API key required.
  const [mapOpen, setMapOpen] = useState(false);
  // A9: check-in QR — opens a large scannable code (encodes ת"ז + נייד) for the kiosk.
  const [qrOpen, setQrOpen] = useState(false);
  // instructions "read more": show ~2 lines, then "עוד" expands the full text (like the maps-nav toggle).
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesOverflow, setNotesOverflow] = useState(false);
  const notesRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  // הנחיית ברירת-מחדל, כשליומן לא הוגדרה הנחייה משלו
  const note = t("appointment.defaultNote");

  const visitor = user?.visitors?.find(v => v.id == location.pathname.replace('/appointment/', ''));
  const { id } = useParams();
  const appointment = user?.visitors?.find((v) => v.id === parseInt(id));
  // true once the citizen's appointments have actually loaded (array present, even if empty).
  const dataLoaded = Array.isArray(user?.visitors);

  const DeleteFunc = () => {
    dispatch(deleteAppointment(visitor.id));
  };

  // Only bounce to the list AFTER the data has loaded. A reminder/booking deep-link can mount this
  // screen while the session is still fetching (getHomeData when a valid token already exists) —
  // bouncing on that empty interim state is what sometimes landed the citizen on "my appointments"
  // instead of the guidance screen. While loading we wait; if the appointment genuinely isn't theirs,
  // we navigate home once the data is in.
  useEffect(() => {
    if (dataLoaded && !appointment) {
      navigate('/');
    }
  }, [dataLoaded, appointment, navigate]);

  // show the "עוד" toggle only when the instructions text actually exceeds the 2-line clamp.
  useEffect(() => {
    const el = notesRef.current;
    if (el) setNotesOverflow(el.scrollHeight > el.clientHeight + 2);
  }, [visitor, notesOpen, appointment]);

  if (!appointment) {
    return null; // Or a loading indicator while navigating
  }

  // Edit screen renders INLINE inside the same .content (height:100dvh) container as
  // the booking flow — NOT inside a MUI Dialog. The dialog used height:100%, which on
  // mobile extends behind the browser toolbar and pushed the "אישור" CTA off the
  // visible bottom (and added a Paper border/radius → corner lines). Inline = pixel
  // identical to "קביעת תור חדש".
  if (editOpen) {
    return <Calendar visitor={visitor} close={() => setEditOpen(false)} />;
  }

  const isFuture = moment(appointment.appointed_time).isAfter(moment());
  const type = isFuture ? 'future' : 'past';
  /* ★★ שם המחלקה נלקח **מהתור עצמו** ורק בהיעדרו מרשימת-ההזמנה.
     ⚠️ הבאג שזה סוגר (דיווח-משתמש 06/08/2026): `user.departments` היא
        רשימת המחלקות ש**אפשר להזמין מהן** (מסוננת ב-`is_active`). ברגע
        שהמנהל סגר מחלקה, שם-המחלקה נעלם גם מתורים **קיימים ותקפים** שכבר
        נקבעו — התור נשאר, הזיהוי שלו על המסך נעלם.
     ★ הנפילה-לאחור נשמרת כדי שהמסך יעבוד גם מול בקנד ישן יותר, שאינו
       שולח `department_title`. */
  const apptDept = appointment.department_title
    ? { title: appointment.department_title }
    : user?.departments?.find((d) => d.id === appointment.department_id);
  const providerName = appointment.schedule?.title || '';
  const address = appointment.schedule?.address || '';
  const providerLocation = appointment.schedule?.location || '';
  const phone = appointment.schedule?.phone || '';
  const email = appointment.schedule?.email || '';
  // A9+ check-in QR payload: the SIGNED token `A1.<id>.<sig>` minted by the server (Otp::generateOutput).
  // It is an HMAC, so it cannot be composed here — the browser must never hold the tenant secret. It
  // replaces the original A9 payload (the citizen's ת"ז + נייד in cleartext, unsigned and forgeable).
  // A tenant whose backend predates this field simply shows no QR rather than a stale insecure one.
  const qrValue = String(appointment.qr_token || '').trim();
  // הגרסה הקריאה של אותו זיהוי (XXXX-YYYY) — מוצגת מתחת ל-QR בפופאפ
  const qrCode = String(appointment.qr_code || '').trim();
  const showQr = isFuture && qrValue !== '';
  const mapsUrl = (mode) =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=${mode}`;
  // keyless embeddable map for the address (Hebrew UI). Loaded only when the user opens it.
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}&z=16&hl=iw&output=embed`;
  // Waze deep-link — opens the Waze app (or web) navigating to the address.
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  // Open the Waze APP first (waze:// scheme). If it isn't installed nothing takes over,
  // so after a short delay fall back to the web link. If the app opens, the page is
  // backgrounded → visibilitychange cancels the fallback.
  const openWaze = (e) => {
    if (e) e.preventDefault();
    if (!address) return;
    const q = encodeURIComponent(address);
    const appUrl = `waze://?q=${q}&navigate=yes`;
    const webUrl = `https://waze.com/ul?q=${q}&navigate=yes`;
    // fall back to the web link in a NEW tab so the appointment page is preserved.
    const fallback = setTimeout(() => { window.open(webUrl, '_blank', 'noopener'); }, 1400);
    const cancel = () => { if (document.hidden) clearTimeout(fallback); };
    document.addEventListener('visibilitychange', cancel, { once: true });
    window.location.href = appUrl;
  };
  // calendar event body: pre-visit instructions + the provider's phone/email (NOT sent in the SMS).
  const calDetails = [
    appointment.mynote || '',
    phone ? t('appointment.calendarPhone', { phone }) : '',
    email ? t('appointment.calendarEmail', { email }) : '',
  ].filter(Boolean).join('\n');

  return (
    <div className="form_container appt-detail">
      {!editOpen && !delOpen && (
        <Button
          aria-label={t('common.back')}
          onClick={() => navigate('/')}
          className="back_btn"
          size="small"
          {...backIconProps}
        >
          {t('common.back')}
        </Button>
      )}

      {/* pre-visit info — upcoming appointments only, laid out as clear sections */}
      {isFuture && (
        <div className="appt-sections">

          {/* 1. הנחיות לפני הביקור — עם תאריך+שעת התור צמודים לכותרת */}
          <div className="appt-card">
            <div className="appt-card-head"><InfoOutlinedIcon fontSize="small" /> {t('appointment.instructionsTitle')}
              <span className="appt-head-when">
                <span className="appt-chip appt-date"><EventRoundedIcon className="appt-chip-icon" />{moment(appointment.appointed_time).format('DD/MM/YYYY')}</span>
                <span className="appt-chip appt-time"><AccessTimeRoundedIcon className="appt-chip-icon" />{moment(appointment.appointed_time).format('HH:mm')}</span>
              </span>
            </div>
            <div className="appt-card-body">
              {/* Notes may carry non-breaking spaces from the admin textarea; convert to normal spaces
                  so they wrap, and keep intended line breaks as <br>. */}
              <div ref={notesRef} className={`notes${notesOpen ? ' notes--open' : ''}`} dangerouslySetInnerHTML={{ __html: (visitor.mynote || note).replace(/&nbsp;|&#160;|&#xa0;/gi, ' ').replace(/ /g, ' ').replace(/\r\n|\r|\n/g, '<br>') }}></div>
              {(notesOverflow || notesOpen) && (
                <button type="button" className={`notes-more${notesOpen ? ' is-open' : ''}`} onClick={() => setNotesOpen(o => !o)} aria-expanded={notesOpen}>
                  <span>{notesOpen ? t('appointment.less') : t('appointment.more')}</span>
                  <ExpandMoreRoundedIcon className="notes-more-chevron" fontSize="small" />
                </button>
              )}
            </div>
          </div>

          {/* צ'ק-אין מהיר (QR) — מתחת להנחיות. קוד לסריקה בעמדת-הקיוסק. לחיצה מגדילה. */}
          {showQr && (
            <button type="button" className="appt-card appt-qr-card" onClick={() => setQrOpen(true)}>
              <span className="appt-qr-mini">
                <QRCodeSVG value={qrValue} size={50} level="M" />
              </span>
              <span className="appt-qr-txt">
                <b>{t('appointment.quickCheckin')}</b>
                <small>{t('appointment.quickCheckinSub')}</small>
              </span>
              <span className="appt-qr-cta">{t('appointment.enlarge')}</span>
            </button>
          )}

          {/* הוספה ליומן — מעל כרטיס נותן-השירות/מחלקה */}
          <div className="appt-card">
            <div className="appt-card-head"><EventAvailableIcon fontSize="small" /> {t('appointment.addToCalendar')}</div>
            <div className="appt-card-body appt-card-body--calendar">
              <AddToCalendar
                title={t('appointment.calendarEventTitle', {
                  title: `${apptDept?.title || ''}${providerName ? ' - ' + providerName : ''}`,
                })}
                start={moment(appointment.appointed_time).toDate()}
                durationMinutes={appointment.duration || 15}
                location={address}
                details={calDetails}
              />
            </div>
          </div>

          {/* נותן השירות — שם (כותרת) + פרטי קשר + כתובת + ניווט Google Maps */}
          {(providerName || phone || email || address || providerLocation) && (
            <div className="appt-card">
              <div className="appt-card-head">
                <ContactPhoneRoundedIcon fontSize="small" />
                <span className="appt-provider-title">
                  {apptDept?.title && (
                    <>
                      <span className="appt-dept">{apptDept.title}</span>
                      <span className="appt-sep">·</span>
                    </>
                  )}
                  {providerName || t('appointment.providerFallback')}
                </span>
              </div>
              <div className="appt-card-body">
                <div className="provider-contact-wrap">
                  <div className="provider-contact-info">
                {phone && (
                  <div className="provider-contact-row">{t('common.phone')}: <a href={`tel:${phone}`} dir="ltr">{phone}</a></div>
                )}
                {email && (
                  <div className="provider-contact-row">{t('common.email')}: <a href={`mailto:${email}`} dir="ltr">{email}</a></div>
                )}
                {address && (
                  <div className="provider-contact-row">{t('common.address')}: {address}</div>
                )}
                {providerLocation && (
                  <div className="provider-contact-row">{t('common.notes')}: {providerLocation}</div>
                )}
                  </div>
                  {/* Waze — big icon on the LEFT of the contact block. NO target="_blank":
                      a same-context tap lets the phone intercept the universal link and open
                      the Waze app (a new tab breaks that). Gated on address. */}
                  {address && (
                    <a className="provider-waze-big" href={wazeUrl} onClick={openWaze} title={t('nav.wazeTitle')} aria-label={t('nav.wazeTitle')}>
                      <img src="/waze.svg" alt="Waze" className="provider-waze-img" />
                      <span className="provider-waze-label">Waze</span>
                    </a>
                  )}
                </div>
                {address && (
                  <div className="provider-maps">
                    <div className="maps-nav-title">{t('nav.mapsTitle')}</div>
                    <div className="maps-nav-links">
                      <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('driving')}>
                        <DirectionsCarIcon fontSize="small" /> {t('nav.car')}
                      </a>
                      <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('transit')}>
                        <DirectionsBusIcon fontSize="small" /> {t('nav.transit')}
                      </a>
                      <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('walking')}>
                        <DirectionsWalkIcon fontSize="small" /> {t('nav.walk')}
                      </a>
                      {/* "מפה" opens the full-screen overlay (kept compact so the extra links row fits). */}
                      <button type="button" className="maps-nav-link maps-nav-mapbtn" onClick={() => setMapOpen(true)}>
                        <MapRoundedIcon fontSize="small" /> {t('nav.map')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {type === 'future' && (
        <div className="control-inputs">
          <Button variant="contained" className="action-edit-btn" startIcon={<EditRoundedIcon />} onClick={() => setEditOpen(true)}>{t('appointment.editAppointment')}</Button>
          <Button variant="outlined" className="action-del-btn" startIcon={<CloseRoundedIcon />} onClick={() => setDelOpen(true)}>{t('appointment.cancelAppointment')}</Button>
        </div>
      )}

      <Dialog
        open={delOpen}
        onClose={() => setDelOpen(false)}
        disablePortal
        className="del_dialog"
        PaperProps={{
          className: 'custom-dialog',
        }}
      >
        <DialogContent id="alert-del-dialog">
          <DialogContentText>
            <b className="delTor">{t('appointment.confirmCancelTitle')}</b>
          </DialogContentText>
          <div className="button-container">
            <Button variant="contained" className="del-btn" onClick={DeleteFunc}>
              {t('appointment.confirmCancelYes')}
            </Button>
            <Button
              variant="text"
              className="close-btn"
              onClick={() => setDelOpen(false)}
            >
              {t('common.back')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* A6: embedded Google map in an overlay dialog (opens above the page) */}
      <Dialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        maxWidth={false}
        className="map_dialog"
        PaperProps={{ className: 'map-dialog-paper' }}
      >
        <div className="map-dialog-head">
          <span>{address}</span>
          <button type="button" className="map-dialog-close" aria-label={t('common.close')} onClick={() => setMapOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>
        {mapOpen && (
          <iframe
            title={t('nav.map')}
            className="map-dialog-frame"
            src={mapEmbedUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        )}
      </Dialog>

      {/* A9: check-in QR — large scannable code for the kiosk (ת"ז + נייד) */}
      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth={false}
        className="qr_dialog"
        PaperProps={{ className: 'qr-dialog-paper' }}
      >
        <div className="qr-dialog-inner">
          <button type="button" className="qr-dialog-close" aria-label={t('common.close')} onClick={() => setQrOpen(false)}>
            <CloseRoundedIcon fontSize="small" />
          </button>
          <div className="qr-dialog-title">{t('appointment.qrTitle')}</div>
          <div className="qr-dialog-sub">{t('appointment.qrSub')}</div>
          <div className="qr-dialog-code">
            <QRCodeSVG value={qrValue} size={232} level="M" includeMargin />
          </div>
          {qrCode !== '' && (
            /* אותו זיהוי בגרסה קריאה — לנציג שלא מצליח לסרוק (מסך שבור/סוללה ריקה) */
            <>
              <div className="qr-dialog-codelbl">{t('appointment.qrCodeLabel')}</div>
              <div className="qr-dialog-codeval">{qrCode}</div>
            </>
          )}
        </div>
      </Dialog>

      {!editOpen && !delOpen && (
        <Button variant="contained" className="next-btn" onClick={() => navigate('/')}>{t('common.back')}</Button>
      )}
    </div>
  );
}

export default Appointment;
